// ─────────────────────────────────────────────
//  useSecondDepartCommit — what happens when the LOWER lane of a paired room is
//  let go.
//
//  ⚠️ IT IS NOT `useCaseCommit`, AND THAT IS THE WHOLE POINT. The upper lane
//  moves the ROOM'S CASE WINDOW: it can collide with the next case, it changes
//  when a room is running, and it pages the people standing in it after an undo
//  window. The lower lane moves ONE PERSON'S LEAVING TIME. Nothing collides,
//  no room changes hours, and nobody's phone should ring because a board runner
//  corrected the minute a second CRNA goes home. Routing the second lane
//  through the case commit would have written `startTime`/`endTime` on the
//  assignment — the seat's fact — from a gesture about somebody else.
//
//  ⚠️ THE FIELD NAME IS NOT SPELLED HERE. `domain/secondProviderWrite`
//  (`setSecondDepartPatch`) owns it, next to the other second-provider writes,
//  because a second place that knows the string `orienteeDepartTime` is how a
//  surface starts writing a home nothing else reads.
// ─────────────────────────────────────────────
import { useCallback, useRef } from "react";
import { updateAssignmentRow } from "../../firebase/firestore";
import { setSecondDepartPatch } from "../../domain/secondProviderWrite";
import { to12h, hmToMinutes } from "../../domain/timeline";

export default function useSecondDepartCommit({ toast, guard }) {
  // ⚠️ NO LIVE REGION OF ITS OWN. `ToastContext` already renders an
  // `aria-live="polite"` container and the success toast below goes through it,
  // so the change is announced exactly once. `CaseDragStatus` owns the other
  // one, for the case commit; a third would announce a single drag twice, which
  // is the bug the shared `Alert` component already taught this repo.
  //
  // Held in a ref so `commit` keeps ONE identity. The page passes an inline
  // arrow (it closes over the live connection band), and listing it as a
  // dependency would mint a new `commit` on every render — which then re-renders
  // every bar on the chart, mid-drag included. Same reason as `useCaseCommit`.
  const guardRef = useRef(guard);
  guardRef.current = guard;

  const commit = useCallback(
    async ({ caseRow, departTime, person }) => {
      if (!caseRow || !caseRow.id || !departTime) return false;
      if (guardRef.current && !guardRef.current()) return false;

      const who = String(person?.name || "").trim() || "The second provider";
      const where = caseRow.roomName || "that room";
      try {
        await updateAssignmentRow(caseRow.id, setSecondDepartPatch(departTime));
      } catch (err) {
        console.error("[Timeline] second-provider leaving time write failed:", err);
        if (toast) toast.error("That leaving time could not be saved. Please try again.");
        return false;
      }
      const spoken = to12h(hmToMinutes(departTime)) || departTime;
      if (toast) toast.success(`${who} now leaves ${where} at ${spoken}.`);
      return true;
    },
    [toast]
  );

  return { commit };
}
